import React, { useState } from "react";
import { X, Clock, AlertTriangle } from "lucide-react";
import { useCreateBanRequest } from "../../hooks/useBanRequests";
import { Customer } from "../../types/customer";

interface BanOptionsModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

export const BanOptionsModal: React.FC<BanOptionsModalProps> = ({
  customer,
  isOpen,
  onClose,
}) => {
  const [selectedBanType, setSelectedBanType] = useState<
    "temporary_ban" | "permanently_banned"
  >("temporary_ban");
  const [banEndDate, setBanEndDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createBanRequestMutation = useCreateBanRequest();

  if (!customer) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!remarks.trim()) return;
    if (selectedBanType === "temporary_ban" && !banEndDate) return;

    setIsSubmitting(true);
    try {
      await createBanRequestMutation.mutateAsync({
        customerId: customer.id,
        banType: selectedBanType,
        banEndDate:
          selectedBanType === "temporary_ban" ? banEndDate : undefined,
        remarks: remarks.trim(),
      });

      setRemarks("");
      setBanEndDate("");
      setSelectedBanType("temporary_ban");
      onClose();
    } catch (error) {
      console.error("Failed to create ban request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRemarks("");
    setBanEndDate("");
    setSelectedBanType("temporary_ban");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h2 className="text-lg font-semibold text-white">
                Blacklist Customer
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Customer Info */}
            <div className="mb-4 p-3 bg-slate-700 rounded-lg">
              <div className="text-sm text-slate-400">Customer</div>
              <div className="text-white font-medium">{customer.name}</div>
              <div className="text-sm text-slate-300">{customer.email}</div>
            </div>

            {/* Ban Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Ban Type *
              </label>
              <div className="space-y-3">
                {/* Temporary Ban Option */}
                <label className="flex items-start gap-3 p-3 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600 transition-colors">
                  <input
                    type="radio"
                    name="banType"
                    value="temporary_ban"
                    checked={selectedBanType === "temporary_ban"}
                    onChange={(e) =>
                      setSelectedBanType(e.target.value as "temporary_ban")
                    }
                    className="w-4 h-4 text-orange-600 bg-slate-600 border-slate-500 focus:ring-orange-500 focus:ring-2 mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-orange-400" />
                      <span className="text-white font-medium">
                        Temporary Ban
                      </span>
                    </div>
                    <p className="text-sm text-slate-300">
                      Temporarily ban this customer until a specific date
                    </p>
                  </div>
                </label>

                {/* Permanent Ban Option */}
                <label className="flex items-start gap-3 p-3 bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-600 transition-colors">
                  <input
                    type="radio"
                    name="banType"
                    value="permanently_banned"
                    checked={selectedBanType === "permanently_banned"}
                    onChange={(e) =>
                      setSelectedBanType(e.target.value as "permanently_banned")
                    }
                    className="w-4 h-4 text-red-600 bg-slate-600 border-slate-500 focus:ring-red-500 focus:ring-2 mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-white font-medium">
                        Permanent Ban
                      </span>
                    </div>
                    <p className="text-sm text-slate-300">
                      Permanently ban this customer from the platform
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Ban End Date (only for temporary ban) */}
            {selectedBanType === "temporary_ban" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Ban Until *
                </label>
                <input
                  type="datetime-local"
                  value={banEndDate}
                  onChange={(e) => setBanEndDate(e.target.value)}
                  required
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            )}

            {/* Remarks */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Reason / Remarks *
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Please provide a detailed reason for this ban request..."
                required
                rows={4}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !remarks.trim() ||
                  (selectedBanType === "temporary_ban" && !banEndDate)
                }
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedBanType === "temporary_ban"
                    ? "bg-orange-600 hover:bg-orange-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
